import React from "react";
import classNames from "classnames";
import throttle from "lodash.throttle";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon";
import { PublicationI } from "@PublicationReader/@types/Publication";
import { Tooltip } from "@PublicationReader/components/Tooltip";
import { downloadPdf } from "@PublicationReader/helpers";
import { ReaderContext } from "@PublicationReader/context";
import { AppProps } from "../../App";

interface HeaderProps extends Pick<AppProps, "apiDownloadUrl"> {
  closeHandler: () => void;
  publication: PublicationI;
}

const Header = ({ publication, closeHandler, apiDownloadUrl }: HeaderProps) => {
  const { t } = useTranslation("common");

  const { title, browserPrintUrl } = publication;
  const {
    setTocIsOpen,
    tocIsOpen,
    setDownloadModalIsOpen,
    setDownloadPdfProgress,
  } = React.useContext(ReaderContext);
  const ref = React.useRef<HTMLHeadElement>(null);
  const [isSticky, setIsSticky] = React.useState(false);

  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const handleScroll = throttle(() => {
    const scrollY =
      document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (ref.current) {
      // If scroll passed header offset + top margin
      if (scrollY > ref.current.offsetTop + 30) {
        document.body.classList.add("pubr-sticky");
        setIsSticky(true);
      } else {
        document.body.classList.remove("pubr-sticky");
        setIsSticky(false);
      }
    }
  }, 100);

  React.useEffect(() => {
    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleDownload = async () => {
    // Show progress in download modal
    setDownloadPdfProgress("inProgress");
    setDownloadModalIsOpen(true);

    // Download pdf
    await downloadPdf(apiDownloadUrl, publication);

    // Show completion message and close modal + tooltip
    setDownloadPdfProgress("complete");
    setDownloadModalIsOpen(true);
    setTooltipOpen(false);
  };

  return (
    <header
      className={classNames(
        "pubr-header",
        isSticky && "pubr-header--sticky pubr-header--shadow"
      )}
      ref={ref}
    >
      <div className="pubr-header__inner">
        <div className="pubr-header__inner-wrap">
          {/* Table of contents trigger */}
          <button
            onClick={() => setTocIsOpen(true)}
            aria-label={t("openToc") as string}
            aria-controls="pubr-toc"
            aria-expanded={tocIsOpen}
            className="pubr-icon-button pubr-icon-button--nav"
          >
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Print trigger */}
          <button
            onClick={() => {
              window.open(browserPrintUrl, "_blank");
            }}
            aria-label={t("printerFriendlyVersion") as string}
            className="pubr-icon-button pubr-icon-button--print"
          >
            <Icon name="print" />
          </button>
          <strong
            className={classNames(
              "pubr-header__title",
              !isSticky && "pubr-header__title--hidden"
            )}
          >
            {title}
          </strong>

          {/* Download trigger */}
          <button
            className="pubr-icon-button pubr-icon-button--download"
            aria-label={t("downloadPublication") as string}
            data-tooltip-id="pubr-download"
            data-tooltip-place="bottom"
            onClick={() => setTooltipOpen(!tooltipOpen)}
          >
            <Icon name="download" />
          </button>
          <Tooltip id="pubr-download" asNav clickable isOpen={tooltipOpen}>
            <button onClick={handleDownload}>
              {t("downloadPublication") as string}
            </button>
            <button
              onClick={() => {
                setDownloadModalIsOpen(true);
                setTooltipOpen(false);
              }}
            >
              {t("selectSection")}
            </button>
          </Tooltip>

          {/* Close trigger */}
          <button
            className="pubr-icon-button"
            onClick={closeHandler}
            aria-label={t("closePublication") as string}
          >
            <Icon name="cross" />
          </button>
        </div>
      </div>
    </header>
  );
};

export { Header };
